import useRequest from "@/lib/request/useRequest";
import { useMemo, useState, useCallback } from "react";
import { WaterRiskZones } from "@/types/water";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { scaleTime } from "d3-scale";
import { Switch, RangeValue, DateValue } from "@heroui/react";
import { useI18n } from "@/lib/i18n/useI18n";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer, ComposedChart, XAxis, YAxis, Area, Line, Tooltip, Legend } from "recharts";
import dayjs from "dayjs";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

function SoilWaterContent({ fieldId, dateRange }: { fieldId: string | undefined, dateRange: RangeValue<DateValue> | null }) {
    const { useGetRequest } = useRequest();
    const { formatDateMMDDYYYY, formatUnit } = useI18n();
    const { t } = useTranslation();
    const waterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
    });
    const waterContentData = useMemo(() => {
        return sortArrayByKey(
            waterContentQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                    range: [d.swc_sig_under, d.swc_sig_over],
                    green: [d.rtsml_b, d.rtsml_t],
                    red: [0, d.rtsml_b],
                    blue: [d.rtsml_t, d.fhc],
                })) ?? [],
            "time",
        );
    }, [waterContentQuery.data?.data, dateRange]);

    const { setSelectedDate } = useFieldDashboard() ?? {};
    const [showSWCRanges, setShowSWCRanges] = useState(false);

    const maxValue = Math.max(
        Math.ceil(
            Math.max(...waterContentData.map((d) => d.time as number)) / 2,
        ) * 2,
    );
    const minValue = Math.min(
        ...waterContentData.map((d) => d.time as number),
    );
    const timeScale = scaleTime().domain([minValue, maxValue]);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const [minSoilWaterContent, maxSoilWaterContent] = useMemo(() => {
        return [
            Math.min(
                ...waterContentData
                    .filter((d) => d.swc_sig_under !== null)
                    .map((d) => d.swc_sig_under as number),
            ),
            Math.max(
                ...waterContentData
                    .filter((d) => d.swc_sig_over !== null)
                    .map((d) => d.swc_sig_over as number),
            ),
        ];
    }, [waterContentData]);

    const onChartClick: CategoricalChartFunc = useCallback((data) => {
        const date = dayjs(data.activePayload?.[0]?.payload?.time).format("YYYY-MM-DD");
        if (date) {
            setSelectedDate?.(date);
        }
    }, [setSelectedDate]);


    return (
        <div>
            <div className="font-semibold">Soil Water Content</div>
            <div className="flex flex-row items-center gap-2 justify-end">
                <div className="text-sm">Uncertainty Threshold</div>
                <Switch
                    size="sm"
                    aria-label="Uncertainty Threshold"
                    isSelected={showSWCRanges}
                    onValueChange={setShowSWCRanges}
                />
            </div>
            {waterContentData.length ? (
                <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart
                        data={waterContentData}
                        syncId="water"
                        onClick={onChartClick}
                        style={{ cursor: "pointer" }}
                    >
                        <XAxis
                            dataKey="time"
                            scale="linear"
                            type="number"
                            spacing={50}
                            angle={-8}
                            fontSize={12}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                            domain={domain}
                            ticks={ticks}
                        />
                        <YAxis
                            fontSize={12}
                            label={{
                                value: t(
                                    "chartLabels.fieldSoilWaterContent",
                                    { ns: "insights" },
                                ),
                                angle: -90,
                                dx: -20,
                            }}
                            domain={[
                                Math.floor(minSoilWaterContent),
                                Math.ceil(maxSoilWaterContent),
                            ]}
                            allowDataOverflow
                            type="number"
                            scale="linear"
                        />
                        <Area
                            dataKey="red"
                            dot={false}
                            strokeWidth={0}
                            fill="red"
                            fillOpacity={0.1}
                            name={t("chartLabels.nearingDepletion", {
                                ns: "insights",
                            })}
                            tooltipType="none"
                            stroke="red"
                            legendType="rect"
                        />
                        <Area
                            dataKey="blue"
                            dot={false}
                            strokeWidth={0}
                            fill="blue"
                            fillOpacity={0.1}
                            name={t("chartLabels.nearingSaturation", {
                                ns: "insights",
                            })}
                            tooltipType="none"
                            stroke="blue"
                            legendType="rect"
                        />
                        <Area
                            dataKey="green"
                            dot={false}
                            strokeWidth={0}
                            fill="green"
                            fillOpacity={0.1}
                            name={t("chartLabels.optimal", {
                                ns: "insights",
                            })}
                            tooltipType="none"
                            stroke="green"
                            legendType="rect"
                        />
                        {showSWCRanges && (
                            <>
                                <Area
                                    dataKey="range"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="black"
                                    fillOpacity={0.2}
                                    tooltipType="none"
                                    legendType="none"
                                />
                            </>
                        )}

                        <Line
                            dataKey="swc_est"
                            stroke="black"
                            dot={false}
                            strokeWidth={3}
                            name={t("chartLabels.soilWaterContent", {
                                ns: "insights",
                            })}
                        />
                        <Tooltip
                            labelFormatter={(t) =>
                                formatDateMMDDYYYY(t)
                            }
                            formatter={(value, name) => [
                                `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                name,
                            ]}
                        />
                        <Legend
                            verticalAlign="top"
                            height={36}
                            fontSize={12}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            ) : (
                <div className="text-sm italic text-center mt-4">No data available</div>
            )}
        </div>
    );
}

export default SoilWaterContent;